/**
 * EditDataSourcePage - edit a source's name and enabled state. Connection
 * details are immutable here (reconnect to change them).
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, TextField, Stack, Button, Switch, FormControlLabel,
  CircularProgress, Alert, useTheme,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import SaveIcon from '@mui/icons-material/Save';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import { ConnectorLogo, typeLabel } from './connectorMeta';
import {
  fetchRequest as getSourceRequest,
  clear as clearGetSource,
} from '../../redux/actions/datasources/getSource';
import {
  fetchRequest as updateSourceRequest,
  clear as clearUpdateSource,
} from '../../redux/actions/datasources/updateSource';

const EMPTY_SLICE = { response: null, isLoading: false, error: null };

export default function EditDataSourcePage() {
  const { sourceId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [source, setSource] = useState(null);

  const [name, setName] = useState('');
  const [active, setActive] = useState(true);

  const getSlice = useSelector((s) => s.datasource_get_source) || EMPTY_SLICE;
  const updateSlice = useSelector((s) => s.datasource_update_source) || EMPTY_SLICE;

  useEffect(() => {
    dispatch(getSourceRequest({ sourceId }));
    return () => {
      dispatch(clearGetSource());
      dispatch(clearUpdateSource());
    };
  }, [sourceId, dispatch]);

  useEffect(() => {
    const s = getSlice.response;
    if (!s) return;
    setSource(s);
    setName(s.name || '');
    setActive(s.status === 'active');
    setLoading(false);
  }, [getSlice.response]);

  useEffect(() => {
    if (getSlice.error) {
      setError(getSlice.error);
      setLoading(false);
    }
  }, [getSlice.error]);

  const save = () => {
    setSaving(true); setError(null);
    // Connection details are immutable here - only name + active state.
    dispatch(updateSourceRequest({
      sourceId,
      body: { name, status: active ? 'active' : 'inactive' },
    }));
  };

  useEffect(() => {
    if (!updateSlice.response) return;
    dispatch(clearUpdateSource());
    navigate(`/datasources/${sourceId}`);
  }, [updateSlice.response, dispatch, navigate, sourceId]);

  useEffect(() => {
    if (updateSlice.error) {
      setError(updateSlice.error);
      setSaving(false);
      dispatch(clearUpdateSource());
    }
  }, [updateSlice.error, dispatch]);

  const card = { border: `1px solid ${theme.palette.divider}`, borderRadius: 2, bgcolor: theme.palette.background.paper };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  if (!source) return (
    <Box sx={{ maxWidth: 720, mx: 'auto', mt: 6 }}>
      <Alert severity="error">{error || 'Source not found.'}</Alert>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <PageBreadcrumbs items={[
        { text: 'Data Sources', link: '/datasources', icon: <StorageIcon /> },
        { text: source.name, link: `/datasources/${sourceId}` },
        { text: 'Edit' },
      ]} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ ...card, p: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <ConnectorLogo type={source.connector_type} size={40} />
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{typeLabel(source.connector_type)}</Typography>
            <Typography variant="caption" color="text.secondary">{sourceId}</Typography>
          </Box>
        </Stack>

        <TextField fullWidth size="small" label="Name" value={name} onChange={(e) => setName(e.target.value)} sx={{ mb: 2 }} />
        <FormControlLabel control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
          label={active ? 'Active' : 'Inactive (paused)'} />
      </Paper>

      <Stack direction="row" spacing={1.5} sx={{ mt: 3 }} justifyContent="flex-end">
        <Button onClick={() => navigate(`/datasources/${sourceId}`)} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} sx={{ textTransform: 'none' }}>
          Save changes
        </Button>
      </Stack>
    </Box>
  );
}
